import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, ViewChild, TemplateRef, ViewContainerRef, AfterViewInit, OnDestroy,
    OnInit, Output, EventEmitter, ElementRef } from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef, ConnectionPositionPair, FlexibleConnectedPositionStrategyOrigin, OverlayConfig } from '@angular/cdk/overlay';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { BaseDirective } from '../../base.directive';

@Component({
    selector: 'eui-popover',
    templateUrl: './eui-popover.component.html',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})

export class EuiPopoverComponent extends BaseDirective implements AfterViewInit, OnDestroy, OnInit {

    @Input() isOpen = false;
    @Input() title: string;
    @Input() position: ('top' | 'right' | 'bottom' | 'left') = 'bottom';
    @Input()
    get hasBackDrop() { return this._hasBackDrop; }
    set hasBackDrop(value) { this._hasBackDrop = coerceBooleanProperty(value); }
    _hasBackDrop = false;
    @Input()
    get hasCloseButton() { return this._hasCloseButton; }
    set hasCloseButton(value) { this._hasCloseButton = coerceBooleanProperty(value); }
    _hasCloseButton = true;

    @Output() outsideClick = new EventEmitter();
    @Output() open = new EventEmitter();
    @Output() close = new EventEmitter();

    public positionClass: string;
    public statesClass: string;

    @ViewChild('templatePortalContent') templatePortalContent: TemplateRef<unknown>;

    private templatePortal: TemplatePortal<any>;
    private overlayRef: OverlayRef;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    private originX: ('start' | 'end' | 'center') = 'center';
    private originY: ('top' | 'bottom' | 'center') = 'bottom';
    private overlayX: ('start' | 'end' | 'center') = 'center';
    private overlayY: ('top' | 'bottom' | 'center') = 'top';
    private fallbackOriginX: ('start' | 'end' | 'center') = 'center';
    private fallbackOriginY: ('top' | 'bottom' | 'center') = 'bottom';
    private fallbackOverlayX: ('start' | 'end' | 'center') = 'center';
    private fallbackOverlayY: ('top' | 'bottom' | 'center') = 'top';

    constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {
        super();
    }

    ngOnInit(): void {
        this.statesClass = [super.getCssClasses('eui-popover')].join(' ').trim();

        if (this.position === 'top') {
            this.originY = 'top';
            this.overlayY = 'bottom';
            this.fallbackOriginY = 'bottom';
            this.fallbackOverlayY = 'top';
        }
        if (this.position === 'right') {
            this.originX = 'end';
            this.overlayX = 'start';
            this.overlayY = 'center';
            this.fallbackOriginX = 'start';
            this.fallbackOverlayX = 'end';
            this.fallbackOverlayY = 'center';
        }
        if (this.position === 'bottom') {
            this.originY = 'bottom';
            this.overlayY = 'top';
            this.fallbackOriginY = 'top';
            this.fallbackOverlayY = 'bottom';
        }
        if (this.position === 'left') {
            this.originX = 'start';
            this.overlayX = 'end';
            this.overlayY = 'center';
            this.fallbackOriginX = 'end';
            this.fallbackOverlayX = 'start';
            this.fallbackOverlayY = 'center';
        }
    }

    ngAfterViewInit(): void {
        this.templatePortal = new TemplatePortal(this.templatePortalContent, this.viewContainerRef);
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.overlayRef = null;
    }

    /**
     * Open a popover
     * @param origin Origin of the popover position
     */
    public openPopover(target: ElementRef): void {
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(target as FlexibleConnectedPositionStrategyOrigin)
            .withPositions([
                new ConnectionPositionPair(
                    { originX: this.originX, originY: this.originY },
                    { overlayX: this.overlayX, overlayY: this.overlayY },
                ),
                new ConnectionPositionPair(
                    { originX: this.fallbackOriginX, originY: this.fallbackOriginY },
                    { overlayX: this.fallbackOverlayX, overlayY: this.fallbackOverlayY },
                ),
            ])
            .withFlexibleDimensions(false)
            .withLockedPosition(true);

        positionStrategy.positionChanges.pipe(takeUntil(this.destroy$)).subscribe((p) => {
            const lastPosition = this.overlayRef.getConfig().positionStrategy['_lastPosition'];
            this.positionClass = this.getPositionClass(lastPosition);
        });

        const scrollStrategy = this.overlay.scrollStrategies.reposition({ scrollThrottle: 10 });

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy,
        });
        this.overlayRef.attach(this.templatePortal);

        this.overlayRef.outsidePointerEvents().pipe(takeUntil(this.destroy$)).subscribe((e) => {
            this.outsideClick.emit();
            this.closePopover();
        });

        this.open.emit();
    }

    /**
     * Close a popover
     */
    public closePopover(): void {
        this.overlayRef.dispose();
        this.overlayRef = null;
        this.close.emit();
    }

    private getPositionClass(lastPosition: ConnectionPositionPair): string {
        if (this.position === 'top' && lastPosition.originY === 'top') {
            return 'top';
        }
        if (this.position === 'top' && lastPosition.originY === 'bottom') {
            return 'bottom';
        }
        if (this.position === 'bottom' && lastPosition.originY === 'bottom') {
            return 'bottom';
        }
        if (this.position === 'bottom' && lastPosition.originY === 'top') {
            return 'top';
        }

        if (this.position === 'left' && lastPosition.originX === 'start') {
            return 'left';
        }
        if (this.position === 'left' && lastPosition.originX === 'end') {
            return 'right';
        }
        if (this.position === 'right' && lastPosition.originX === 'end') {
            return 'right';
        }
        if (this.position === 'right' && lastPosition.originX === 'start') {
            return 'left';
        }
    }

}
